import { type FC, useState } from "react";
import { Form, ListGroup } from "react-bootstrap";
import type { TikTokAdsReallocationAction } from "shared";
import { Modal, useModal } from "../common/modal/Modal";
import { Button } from "../components/Button";

type Props = {
	summary: string;
	actions: TikTokAdsReallocationAction[];
	onConfirm: (selected: TikTokAdsReallocationAction[]) => Promise<void>;
};

/**
 * Explicit confirm gate: client checks each pause / budget change before
 * anything is sent to TikTok (PRD Phase 4).
 */
export const ModalTikTokReallocationConfirm: FC<Props> = ({
	summary,
	actions,
	onConfirm,
}) => {
	const { close } = useModal();
	const [selected, setSelected] = useState<Set<string>>(
		() => new Set(actions.map(actionKey)),
	);
	const [pending, setPending] = useState(false);

	const toggle = (key: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const chosen = actions.filter((action) => selected.has(actionKey(action)));

	return (
		<Modal>
			<h2 className="pb-2">Confirm TikTok changes</h2>
			<p className="text-secondary mb-3">{summary}</p>
			<p className="mb-2" style={{ fontSize: "0.9rem" }}>
				Select every action you authorize. Nothing is sent to TikTok until you
				approve.
			</p>
			<ListGroup className="mb-3">
				{actions.map((action) => {
					const key = actionKey(action);
					const label =
						action.type === "PAUSE_ADGROUP"
							? `Pause ad group “${action.adGroupName}”`
							: `Increase “${action.adGroupName}” from $${action.currentBudget ?? "—"} → $${action.proposedBudget}/day`;
					return (
						<ListGroup.Item key={key}>
							<Form.Check
								type="checkbox"
								id={key}
								checked={selected.has(key)}
								onChange={() => toggle(key)}
								label={
									<span>
										<strong>{label}</strong>
										<div
											className="text-secondary"
											style={{ fontSize: "0.8rem", fontWeight: 400 }}
										>
											{action.reason}
										</div>
									</span>
								}
							/>
						</ListGroup.Item>
					);
				})}
			</ListGroup>
			<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
				<Button variant="secondary" onClick={close} disabled={pending}>
					Cancel
				</Button>
				<Button
					disabled={chosen.length === 0 || pending}
					onClick={async () => {
						setPending(true);
						try {
							await onConfirm(chosen);
							close();
						} finally {
							setPending(false);
						}
					}}
				>
					{pending ? "Applying…" : "Approve & Apply"}
				</Button>
			</div>
		</Modal>
	);
};

const actionKey = (action: TikTokAdsReallocationAction) =>
	`${action.type}:${action.adGroupId}`;
